import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface AuditLogItem {
    id: number;
    user_id: number;
    user_name: string;
    directie_id: number | null;
    directie_name: string | null;
    compartiment_id: number | null;
    compartiment_name: string | null;
    functie_id: number | null;
    functie_name: string | null;
    module: string;
    action: string;
    action_label: string;
    actor_type: string;
    actor_type_label: string;
    source: string;
    source_label: string;
    model_type: string;
    short_model_type: string;
    model_id: number;
    model_name: string | null;
    old_values: any | null;
    new_values: any | null;
    result: string;
    result_label: string;
    error_message: string | null;
    ip_address: string | null;
    user_agent: string | null;
    request_id: string | null;
    has_diff: boolean;
    created_at: string;
    updated_at: string;
}

export interface MetaOption {
    value: string;
    label: string;
}

export interface AuditStats {
    total: number;
    by_action: { [key: string]: number };
    by_module: { [key: string]: number };
    by_result: { [key: string]: number };
    by_user: { [key: string]: number };
    by_day: { [key: string]: number };
}

@Injectable({
    providedIn: 'root'
})
export class AuditLogsService {
    private basePath = environment.interop.basePath;
    private apiUrl = `${this.basePath}api/audit-logs`;

    constructor(private http: HttpClient) {}

    /**
     * Obține lista de audit logs (paginată, pentru DataTable)
     */
    getAuditLogs(params: any): Observable<any> {
        return this.http.get<any>(this.apiUrl, {
            params: this.buildParams(params)
        });
    }

    /**
     * Obține detaliile unui log
     */
    getAuditLogDetail(logId: number): Observable<{ success: boolean; data: AuditLogItem }> {
        return this.http.get<{ success: boolean; data: AuditLogItem }>(`${this.apiUrl}/${logId}`);
    }

    /**
     * Obține lista modulelor disponibile
     */
    getModules(): Observable<{ success: boolean; data: MetaOption[] }> {
        return this.http.get<{ success: boolean; data: MetaOption[] }>(`${this.apiUrl}/meta/modules`);
    }

    /**
     * Obține lista acțiunilor disponibile
     */
    getActions(): Observable<{ success: boolean; data: MetaOption[] }> {
        return this.http.get<{ success: boolean; data: MetaOption[] }>(`${this.apiUrl}/meta/actions`);
    }

    /**
     * Obține lista tipurilor de actor
     */
    getActorTypes(): Observable<{ success: boolean; data: MetaOption[] }> {
        return this.http.get<{ success: boolean; data: MetaOption[] }>(`${this.apiUrl}/meta/actor-types`);
    }

    /**
     * Obține lista surselor
     */
    getSources(): Observable<{ success: boolean; data: MetaOption[] }> {
        return this.http.get<{ success: boolean; data: MetaOption[] }>(`${this.apiUrl}/meta/sources`);
    }

    /**
     * Obține lista rezultatelor
     */
    getResults(): Observable<{ success: boolean; data: MetaOption[] }> {
        return this.http.get<{ success: boolean; data: MetaOption[] }>(`${this.apiUrl}/meta/results`);
    }

    /**
     * Obține statistici
     */
    getStats(params?: { start_date?: string; end_date?: string }): Observable<{ success: boolean; data: AuditStats }> {
        return this.http.get<{ success: boolean; data: AuditStats }>(`${this.apiUrl}/stats`, {
            params: params ? this.buildParams(params) : undefined
        });
    }

    /**
     * Export CSV
     */
    exportCsv(params: any): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/export/csv`, {
            params: this.buildParams(params),
            responseType: 'blob'
        });
    }

    /**
     * Export Excel
     */
    exportExcel(params: any): Observable<Blob> {
        return this.http.get(`${this.apiUrl}/export/excel`, {
            params: this.buildParams(params),
            responseType: 'blob'
        });
    }

    /**
     * Download file din blob
     */
    downloadFile(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    /**
     * Build HttpParams din object
     */
    private buildParams(params: any): HttpParams {
        let httpParams = new HttpParams();
        if (params) {
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
                    httpParams = httpParams.set(key, params[key]);
                }
            });
        }
        return httpParams;
    }
}
